<template>
  <div class="product-safety-content nutrients-group">
    <div class="title-area">
      <div class="title">Ürün Besin Değerleri</div>
      <div class="description">
        Ürün besin değerleri, ürünün içeriği ve içerik miktarı hakkında
        bilgilendirme sağlar. Gireceğiniz değerler 100 gr veya ml içindir.
      </div>
    </div>
    <div class="nutrients-group-container">
      <div class="box">
        <DropdownSelectable
          ref="templateProductGroup"
          label="Besin Değerleri"
          id="nutrition-value"
          :dropItems="nutritionOptions"
          :isShowSearch="true"
          :isRadioGroup="false"
          @selectedItems="handleNutritionSelection"
        />
      </div>
      <div class="box" v-show="selectedNutritionFields.length > 0">
        <div class="nutrients-group-rows">
          <div
            class="nutrients-group-row"
            v-for="(row, rowIndex) in chunkedNutritionFields"
            :key="rowIndex"
          >
            <div
              class="nutrients-group-field"
              v-for="field in row"
              :key="field.lookupCodeId"
            >
              <Input
                :label="field.name"
                :id="`nutrition-${field.lookupCodeId}`"
                :value="nutritionValues[`nutrition-${field.lookupCodeId}`]"
                @input="
                  (event) =>
                    (nutritionValues[`nutrition-${field.lookupCodeId}`] =
                      event.target.value)
                "
                @keyup="handleNutritionInput"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, computed, nextTick } from "vue";
import { Input, DropdownSelectable } from "@n11/so-ui-components";
import { useStore } from "vuex";

const store = useStore();
const nutritionOptions = ref([]);
const templateProductGroup = ref(null);
const selectedNutritionFields = ref([]);
const nutritionValues = ref({});

const handleNutritionSelection = async (selectedOption) => {
  selectedNutritionFields.value = selectedOption;
  
  await nextTick();
  if (templateProductGroup.value) {
    const componentInstance = templateProductGroup.value;
    
    if (Array.isArray(componentInstance.selectedItems)) {
      componentInstance.selectedItems.splice(0, componentInstance.selectedItems.length, ...selectedOption);
    }
  }
};

const handleNutritionInput = (event) => {
  const value = event.target.value;
  const fieldId = event.target.id;
  const allowedChars = /^[0-9,]*$/;

  if (!allowedChars.test(value)) {
    const cleanedValue = value.replace(/[^0-9,]/g, "");
    nutritionValues.value[fieldId] = cleanedValue;
  } else {
    nutritionValues.value[fieldId] = value;
  }
};
onMounted(async () => {
  const response = await store.dispatch("products/getProductNutritionOptions");
  if (response && response["nutritional-values"]) {
    nutritionOptions.value = response["nutritional-values"].map((item) => ({
      id: item.label,
      name: item.label,
      lookupCodeId: item.value,
    }));
  }
});

const chunkedNutritionFields = computed(() => {
  const chunks = [];
  for (let i = 0; i < selectedNutritionFields.value.length; i += 3) {
    chunks.push(selectedNutritionFields.value.slice(i, i + 3));
  }
  return chunks;
});

const nutritionValuesForRequest = computed(() => {
  return selectedNutritionFields.value
    .filter((field) => {
      const values = nutritionValues.value[`nutrition-${field.lookupCodeId}`];
      return values && values.trim() !== "";
    })
    .map((field) => ({
      id: "",
      lookupCodeId: field.lookupCodeId,
      value: nutritionValues.value[`nutrition-${field.lookupCodeId}`],
    }));
});

const loadTemplateData = async (nutritionValuesData) => {
  try {
    if (!nutritionValuesData || !Array.isArray(nutritionValuesData)) {
      return;
    }

    if (nutritionOptions.value.length === 0) {
      try {
        const response = await store.dispatch("products/getProductNutritionOptions");
        if (response && response["nutritional-values"]) {
          nutritionOptions.value = response["nutritional-values"].map((item) => ({
            id: item.label,
            name: item.label,
            lookupCodeId: item.value,
          }));
        }
      } catch (error) {
        console.error('ProductNutritionTable: nutritionOptions yüklenirken hata:', error);
      }
      
      // Eğer hala yüklenmemişse, bir süre bekle
      if (nutritionOptions.value.length === 0) {
        await nextTick();
        let retries = 0;
        while (nutritionOptions.value.length === 0 && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }
      }
    }

    // Besin değerlerini yükle
    const loadedFields = [];
    const loadedValues = {};

    nutritionValuesData.forEach((item) => {
      const lookupCodeId = item.nutritionCode?.value || item.lookupCodeId;
      const value = item.nutritionValue || item.value;
      
      if (lookupCodeId && value) {
        const itemLookupCodeId = String(lookupCodeId);
        
        const option = nutritionOptions.value.find(
          (opt) => String(opt.lookupCodeId) === itemLookupCodeId || opt.lookupCodeId === lookupCodeId
        );
        
        if (option) {
          // Eğer daha önce eklenmemişse ekle
          if (!loadedFields.find((f) => String(f.lookupCodeId) === itemLookupCodeId)) {
            loadedFields.push(option);
          }
          // Değeri set et
          loadedValues[`nutrition-${lookupCodeId}`] = value;
        }
      }
    });

    // Seçili alanları ve değerleri set et
    if (loadedFields.length > 0) {
      // Önce değerleri set et
      nutritionValues.value = { ...nutritionValues.value, ...loadedValues };
      
      await nextTick();
      selectedNutritionFields.value = loadedFields;
      
      await nextTick();
      await nextTick(); // Component'in render olması için ekstra bekleme
      
      if (templateProductGroup.value) {
        const componentInstance = templateProductGroup.value;
        
        if (typeof componentInstance.clearSelection === 'function') {
          componentInstance.clearSelection();
          await nextTick();
        }
        
        if (typeof componentInstance.selectItem === 'function') {
          loadedFields.forEach((field) => {
            const mockEvent = {
              target: {
                checked: true
              }
            };
            componentInstance.selectItem(field, mockEvent);
          });
        }
      }
      
      handleNutritionSelection(loadedFields);
    }
  } catch (error) {
    console.error('ProductNutritionTable template data yüklenirken hata:', error);
  }
};

defineExpose({
  nutritionValuesForRequest,
  loadTemplateData,
});
</script>
<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.nutrients-group {
  margin: 24px 0px;

  &-fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 33%;
  }

  &-rows {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  &-field {
    width: 100%;
  }

  &-container {
    display: flex;
    gap: 24px;
  }

  .box {
    display: flex;
    flex: 1;
    gap: 16px;

    &:first-of-type {
      height: 50px;
      flex: 0 0 calc(50% - 12px);
    }
  }
}
.product-safety-content {
  flex: 1;

  .title-area {
    .title {
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
      margin-bottom: 8px;
    }
    .description {
      font-size: 11px;
      line-height: 14px;
      height: 30px;
    }
  }
  &-form-area {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 16px 0px;
    padding: 24px;
    border-radius: 8px;
    border: 1px solid $colorM10;
    background-color: #f8f7fc;
  }
}
.product-safety-content-boxes {
  textarea:not(:last-child) {
    margin-bottom: 10px;
  }
}
</style>
